import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { CommunityShell, C } from "../../components/CommunityShell";

const TRACKED = [
  { title: "Module engagement", body: "Which modules people open, how long they stay, and which conditions they explore — Explain, Symptoms, Labs, Medications, Cooking, History, Resources, Household." },
  { title: "Learning events", body: "Every meaningful interaction — a condition opened, a lab decoded, a medication compared — is logged as a de-identified learning event with a timestamp." },
  { title: "Simulated patient encounters", body: "Full conversation sessions with simulated patients, including message counts and completion — the deepest signal of applied understanding." },
  { title: "Quiz & assessment attempts", body: "Knowledge checks across programs — attempts, scores, and improvement over time, reportable at the cohort level." },
  { title: "Symptom & lab lookups", body: "What people are worried about: anonymized symptom searches and lab decodes show the health questions your community is actually asking." },
  { title: "Evidence integrity", body: "Every clinical claim is tied to a source and continuously audited — integrity checks run around the clock so content never drifts from evidence." },
];

function num(v) {
  if (v == null) return "…";
  return v.toLocaleString("en-US");
}

function StatCard({ label, value, note, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border p-5 stat-card" style={{ background: C.CARD, borderColor: C.BORDER }}>
      <p className="text-[30px] sm:text-[38px] font-extrabold tracking-tight leading-none" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{value}</p>
      <p className="text-[13px] font-bold mt-2" style={{ color: C.DEEP }}>{label}</p>
      <p className="text-[12px] leading-snug mt-1" style={{ color: C.SUB }}>{note}</p>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <>
      <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-3" style={{ color: C.ACCENT }}>{eyebrow}</p>
      <h2 className="text-[26px] sm:text-[32px] font-extrabold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{title}</h2>
      {sub && <p className="text-[14.5px] leading-relaxed max-w-2xl mb-7" style={{ color: C.SUB }}>{sub}</p>}
      {!sub && <div className="mb-7" />}
    </>
  );
}

export default function CommunityAnalytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/community/analytics-summary")
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const eng = d?.engagement || {};
  const lib = d?.library || {};
  const ev = d?.evidence || {};

  return (
    <CommunityShell
      testid="community-analytics-page"
      eyebrow="Analytics"
      title="What we track — and why it matters."
      intro="Live, de-identified figures pulled straight from the platform — the same reporting we deliver to schools, hospitals, health plans, and workforce boards."
    >
      <style>{`
        .print-report { display: none; }
        @media print {
          [data-testid="community-top-bar"], .no-print, .screen-only { display: none !important; }
          [data-testid="community-analytics-page"] > section { display: none !important; }
          body { background: #fff !important; }
          .print-report { display: block !important; }
          .print-block { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* ── PRINT-ONLY STAKEHOLDER REPORT ─────────────────────────── */}
      <div className="print-report" data-testid="community-analytics-print-report" style={{ color: "#2B2620", fontFamily: C.SANS }}>
        <div style={{ borderBottom: "3px solid #2B2620", paddingBottom: 14, marginBottom: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: C.ACCENT, margin: 0 }}>
            Maldek · The Community
          </p>
          <h1 style={{ fontFamily: C.DISPLAY, fontSize: 30, fontWeight: 800, margin: "6px 0 4px" }}>
            Stakeholder Analytics Report
          </h1>
          <p style={{ fontSize: 12, color: C.SUB, margin: 0 }}>
            Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            {d?.generated_at ? ` · data refreshed ${new Date(d.generated_at).toLocaleString()}` : ""} · live, always-updated figures
          </p>
        </div>

        <p style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 20 }}>
          Partners receive cohort-level reporting on all of the following —
          de-identified, aggregated, and exportable. No personal health
          information is ever shared.
        </p>

        {[
          {
            title: "Module engagement",
            body: "Which modules people open, how long they stay, and which conditions they explore — Explain, Symptoms, Labs, Medications, Cooking, History, Resources, Household.",
            metrics: [
              [num(eng.learning_events), "total tracked interactions"],
              [num(eng.events_30d), "in the last 30 days"],
            ],
          },
          {
            title: "Learning events",
            body: "Every meaningful interaction — a condition opened, a lab decoded, a medication compared — is logged as a de-identified learning event with a timestamp.",
            metrics: [
              [num(eng.learning_events), "learning events on record"],
              [num(eng.learners), "registered learners"],
            ],
          },
          {
            title: "Simulated patient encounters",
            body: "Full conversation sessions with simulated patients, including message counts and completion — the deepest signal of applied understanding.",
            metrics: [
              [num(eng.sim_sessions), "encounter sessions completed"],
              [num(eng.patient_messages), "messages exchanged"],
            ],
          },
          {
            title: "Quiz & assessment attempts",
            body: "Knowledge checks across programs — attempts, scores, and improvement over time, reportable at the cohort level.",
            metrics: [
              [num(eng.quiz_attempts), "assessment attempts logged"],
            ],
          },
          {
            title: "Symptom & lab lookups",
            body: "What people are worried about: anonymized symptom searches and lab decodes show the health questions your community is actually asking.",
            metrics: [
              ["241+", "lab & vital values decoded on demand"],
              [num(lib.conditions), "conditions searchable by symptom"],
            ],
          },
          {
            title: "Evidence integrity",
            body: "Every clinical claim is tied to a source and continuously audited — integrity checks run around the clock so content never drifts from evidence.",
            metrics: [
              [num(ev.cited_claims), "cited clinical claims"],
              [num(ev.evidence_sources), "evidence sources on file"],
              [num(ev.integrity_checks), "integrity checks run"],
            ],
          },
        ].map((s) => (
          <div key={s.title} className="print-block" style={{ border: "1.5px solid #E3D8C2", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
            <h2 style={{ fontFamily: C.DISPLAY, fontSize: 17, fontWeight: 800, margin: "0 0 4px" }}>{s.title}</h2>
            <p style={{ fontSize: 12, lineHeight: 1.55, color: C.SUB, margin: "0 0 10px" }}>{s.body}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 26px" }}>
              {s.metrics.map(([v, lbl]) => (
                <p key={lbl} style={{ margin: 0, fontSize: 12 }}>
                  <span style={{ fontFamily: C.DISPLAY, fontSize: 20, fontWeight: 800, marginRight: 6 }}>{v}</span>
                  <span style={{ color: C.SUB }}>{lbl}</span>
                </p>
              ))}
            </div>
          </div>
        ))}

        <div className="print-block" style={{ background: "#F3E9D6", borderRadius: 10, padding: "12px 16px", marginTop: 6 }}>
          <p style={{ fontSize: 11.5, lineHeight: 1.55, margin: 0, color: C.SUB }}>
            <strong style={{ color: "#2B2620" }}>Privacy commitment:</strong>{" "}
            all figures in this report are aggregate counts. Individual activity
            is never exposed, sold, or shared. Partner reporting is delivered at
            the cohort level only, with a minimum group size to prevent
            re-identification.
          </p>
        </div>
      </div>
      {/* ── END PRINT REPORT ───────────────────────────────────────── */}

      {/* Print bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-10 no-print" data-testid="community-analytics-printbar">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: C.SAGE }} />
          <p className="text-[12px] font-bold uppercase tracking-[0.22em]" style={{ color: C.ACCENT }}>
            Live platform data{d?.generated_at ? ` · updated ${new Date(d.generated_at).toLocaleDateString()}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="community-analytics-print-btn"
          className="px-5 py-2.5 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5"
          style={{ background: C.INK, color: "#FFFDF8", fontFamily: C.SANS }}
        >
          🖨 Print PDF for stakeholders
        </button>
        <Link
          to="/community/partner-reports"
          data-testid="community-analytics-custom-reports-btn"
          className="px-5 py-2.5 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5"
          style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
        >
          Custom facility reports →
        </Link>
      </div>

      {/* 1 — Engagement */}
      <section data-testid="community-analytics-engagement" className="mb-14 screen-only">
        <SectionHead
          eyebrow="Engagement"
          title="People are using it — measurably."
          sub="Every interaction is logged as a de-identified event, so partners can see real usage without ever seeing personal health information."
        />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard testid="ca-stat-learners" value={num(eng.learners)} label="Registered learners" note="Students, community members, and professionals with profiles" />
          <StatCard testid="ca-stat-events" value={num(eng.learning_events)} label="Learning events tracked" note="Condition opens, lab decodes, med comparisons, module time" />
          <StatCard testid="ca-stat-events30" value={num(eng.events_30d)} label="Events in the last 30 days" note="Recent activity window — refreshed live" />
          <StatCard testid="ca-stat-sims" value={num(eng.sim_sessions)} label="Simulated patient encounters" note="Full conversation sessions with virtual patients" />
          <StatCard testid="ca-stat-messages" value={num(eng.patient_messages)} label="Patient-simulation messages" note="Back-and-forth exchanges inside encounters" />
          <StatCard testid="ca-stat-quiz" value={num(eng.quiz_attempts)} label="Quiz & assessment attempts" note="Knowledge checks across all programs" />
        </div>
      </section>

      {/* 2 — Library */}
      <section data-testid="community-analytics-library" className="mb-14 screen-only">
        <SectionHead
          eyebrow="Content library"
          title="Coverage your board can verify."
          sub="Not a pamphlet rack — a clinical-grade library, pre-baked for instant loading and continuously expanded."
        />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard testid="ca-stat-conditions" value={num(lib.conditions)} label="Conditions fully covered" note="100% pre-baked Western + Eastern therapeutic comparisons" />
          <StatCard testid="ca-stat-explainers" value={num(lib.explainers)} label="Deep-dive walkthroughs" note="Full pathophysiology-to-treatment explainers" />
          <StatCard testid="ca-stat-topics" value={num(lib.clinical_topics)} label="Clinical topics released" note="Structured, evidence-locked teaching topics" />
          <StatCard testid="ca-stat-meds" value={num(lib.medication_catalog)} label="Medication catalog entries" note="Searchable drug records with mechanism & safety flags" />
          <StatCard testid="ca-stat-history" value={num(lib.history_articles)} label="Street History documentaries" note="The lived story behind each diagnosis" />
          <StatCard testid="ca-stat-labs" value="241+" label="Lab & vital values" note="Reference ranges with pediatric variants — any lab live-synthesized" />
        </div>
      </section>

      {/* 3 — Evidence */}
      <section data-testid="community-analytics-evidence" className="mb-14 screen-only">
        <SectionHead
          eyebrow="Evidence & integrity"
          title="Every claim is cited. Every citation is audited."
          sub="Health content without sources is a liability. Ours is claim-locked to evidence and machine-audited continuously."
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard testid="ca-stat-claims" value={num(ev.cited_claims)} label="Cited clinical claims" note="Individual statements tied to a specific source" />
          <StatCard testid="ca-stat-sources" value={num(ev.evidence_sources)} label="Evidence sources on file" note="Guidelines, textbooks, and primary literature" />
          <StatCard testid="ca-stat-integrity" value={num(ev.integrity_checks)} label="Integrity checks run" note="Automated audits guarding against content drift" />
        </div>
      </section>

      {/* 4 — What we track */}
      <section data-testid="community-analytics-tracked" className="mb-14 screen-only">
        <SectionHead
          eyebrow="For partners"
          title="Exactly what gets tracked."
          sub="Partners receive cohort-level reporting on all of the following — de-identified, aggregated, and exportable. No personal health information is ever shared."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {TRACKED.map((t) => (
            <div
              key={t.title}
              data-testid={`ca-tracked-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border p-6"
              style={{ background: C.CARD, borderColor: C.BORDER }}
            >
              <h3 className="text-[17px] font-bold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{t.title}</h3>
              <p className="text-[14px] leading-relaxed" style={{ color: C.SUB }}>{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Privacy note (prints too) */}
      <section
        data-testid="community-analytics-privacy"
        className="rounded-2xl border p-6 mb-14 screen-only"
        style={{ background: C.PANEL, borderColor: C.BORDER }}
      >
        <p className="text-[13px] leading-relaxed" style={{ color: C.SUB }}>
          <span className="font-bold" style={{ color: C.INK }}>Privacy commitment:</span>{" "}
          all figures on this page are aggregate counts. Individual activity is
          never exposed, sold, or shared. Partner reporting is delivered at the
          cohort level only, with a minimum group size to prevent re-identification.
        </p>
      </section>

      {/* CTA */}
      <section
        data-testid="community-analytics-cta"
        className="rounded-2xl border p-8 sm:p-10 text-center no-print"
        style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #FAF5EC 100%)", borderColor: C.BORDER }}
      >
        <h2 className="text-[24px] sm:text-[30px] font-extrabold tracking-tight mb-3" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          Bring this to your patients or members.
        </h2>
        <p className="text-[15px] mb-6 max-w-xl mx-auto" style={{ color: C.SUB }}>
          See how doctors' offices, community centers, and health plans put The
          Community in front of the people they serve.
        </p>
        <Link
          to="/community/facilities"
          data-testid="community-analytics-cta-facilities"
          className="inline-block px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
          style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
        >
          Facilities & Partners →
        </Link>
      </section>
    </CommunityShell>
  );
}
